var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useMemo } from "react";
import { AccordionSet, Accordion, Checkbox, RadioButton, Button, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { RecekAutoSuggest, RecekTextField, RecekSelection } from "./inputs";
import { ProvisionalFilterAccordion } from "./filters/ProvisionalFilterAccordion";
import { LastReceivedFilterAccordion } from "./filters/LastReceivedFilterAccordion";
import { useTitleNameSuggestions } from "../hooks/useTitleNameSuggestions";
import { PeriodicalFrequencyEnum, PeriodicalLibraryStatusEnum, } from "../types/Api";
import { formatPublisherLabel } from "../utils/publisherLabel";
import { DEFAULT_VOLUME_STATUSES, } from "../utils/volumeConstants";
import { VolumeStatusBadge } from "./common/VolumeStatusBadge";
import { lastReceivedYearError, } from "../utils/lastReceivedFilter";
import { AgendaStateFilter } from "./agenda/AgendaStateFilter";
var KULT_ORIGINS = [
    "UNESCO",
    "DERIVED",
    "MANUAL",
    "UNRESOLVED",
];
export var PeriodicalFilters = function (_a) {
    var filters = _a.filters, libraryStatus = _a.libraryStatus, publishers = _a.publishers, isPublishersLoading = _a.isPublishersLoading, selectedPublisher = _a.selectedPublisher, onFilterChange = _a.onFilterChange, onFrequencyChange = _a.onFrequencyChange, onKultSubjectGroupOriginChange = _a.onKultSubjectGroupOriginChange, onStatusChange = _a.onStatusChange, onLibraryStatusChange = _a.onLibraryStatusChange, onShowEndedChange = _a.onShowEndedChange, onProvisionalChange = _a.onProvisionalChange, onAgendaStateChange = _a.onAgendaStateChange, onLastReceivedChange = _a.onLastReceivedChange, onPublisherInput = _a.onPublisherInput, onApplyFilters = _a.onApplyFilters, hasUnappliedChanges = _a.hasUnappliedChanges;
    var formatMessage = useIntl().formatMessage;
    var _b = useTitleNameSuggestions({
        scope: "PERIODICALS",
        type: "PERIODICAL",
        libraryStatus: libraryStatus,
    }), suggestions = _b.suggestions, updateSuggestQuery = _b.updateSuggestQuery;
    var lastReceivedYearInvalid = onLastReceivedChange !== undefined &&
        lastReceivedYearError(filters.lastReceivedYear) !== null;
    var publisherOptions = useMemo(function () { return __spreadArray(__spreadArray([
        {
            value: "",
            label: formatMessage({ id: "ui-br-legaldeposits.filter.select.all" }),
        }
    ], (selectedPublisher &&
        selectedPublisher.id &&
        selectedPublisher.name &&
        !publishers.some(function (p) { return p.id === selectedPublisher.id; })
        ? [
            {
                value: selectedPublisher.id,
                label: formatPublisherLabel(selectedPublisher),
            },
        ]
        : []), true), publishers.map(function (p) { return ({
        value: p.id,
        label: formatPublisherLabel(p),
    }); }), true); }, [publishers, selectedPublisher, formatMessage]);
    return (React.createElement("form", { onSubmit: function (e) {
            e.preventDefault();
            onApplyFilters();
        } },
        React.createElement(AccordionSet, null,
            React.createElement(Accordion, { label: formatMessage({
                    id: "ui-br-legaldeposits.filter.name",
                }), separator: false },
                React.createElement(RecekAutoSuggest, { labelId: "ui-br-legaldeposits.filter.name", showLabel: false, value: filters.name, onChange: function (value) {
                        onFilterChange("name", value);
                        updateSuggestQuery(value);
                    }, suggestions: suggestions, placeholder: formatMessage({
                        id: "ui-br-legaldeposits.filter.name.placeholder",
                    }) })),
            React.createElement(Accordion, { label: formatMessage({
                    id: "ui-br-legaldeposits.filter.issn",
                }), separator: false },
                React.createElement(RecekTextField, { labelId: "ui-br-legaldeposits.filter.issn", showLabel: false, value: filters.issn, onChange: function (e) { return onFilterChange("issn", e.target.value); }, placeholder: formatMessage({
                        id: "ui-br-legaldeposits.filter.issn.placeholder",
                    }) })),
            React.createElement(Accordion, { label: formatMessage({
                    id: "ui-br-legaldeposits.filter.publisher",
                }), separator: false },
                React.createElement(RecekSelection, { labelId: "ui-br-legaldeposits.filter.publisher", showLabel: false, asyncFilter: true, value: selectedPublisher ? selectedPublisher.id : "", loading: isPublishersLoading, onFilter: onPublisherInput, onChange: function (value) { return onFilterChange("publisherId", value); }, dataOptions: publisherOptions })),
            React.createElement(Accordion, { label: formatMessage({
                    id: "ui-br-legaldeposits.filter.periodicity",
                }), separator: false }, Object.values(PeriodicalFrequencyEnum).map(function (frequency) { return (React.createElement(Checkbox, { key: frequency, label: formatMessage({
                    id: "ui-br-legaldeposits.periodicals.frequency.".concat(frequency),
                }), checked: filters.frequencies.includes(frequency), onChange: function () { return onFrequencyChange(frequency); } })); })),
            onKultSubjectGroupOriginChange && (React.createElement(Accordion, { label: formatMessage({
                    id: "ui-br-legaldeposits.periodicals.kultSubjectGroup.filter.label",
                }), separator: false }, KULT_ORIGINS.map(function (origin) { return (React.createElement(Checkbox, { key: origin, label: formatMessage({
                    id: "ui-br-legaldeposits.periodicals.kultSubjectGroup.filter.".concat(origin),
                }), checked: filters.kultSubjectGroupOrigins.includes(origin), onChange: function () { return onKultSubjectGroupOriginChange(origin); } })); }))),
            onProvisionalChange && (React.createElement(ProvisionalFilterAccordion, { titleType: "PERIODICAL", checked: filters.provisional, onChange: onProvisionalChange })),
            onAgendaStateChange && (React.createElement(AgendaStateFilter, { listType: "PERIODICALS", value: filters.agendaState, onChange: onAgendaStateChange })),
            onStatusChange && (React.createElement(Accordion, { label: formatMessage({
                    id: "ui-br-legaldeposits.filter.depositStatus",
                }), separator: false }, DEFAULT_VOLUME_STATUSES.map(function (status) { return (React.createElement(Checkbox, { key: status, label: React.createElement(VolumeStatusBadge, { status: status }), checked: filters.statuses.includes(status), onChange: function () { return onStatusChange(status); } })); }))),
            onLastReceivedChange && (React.createElement(LastReceivedFilterAccordion, { values: filters, yearInvalid: lastReceivedYearInvalid, onChange: onLastReceivedChange })),
            onShowEndedChange && (React.createElement(Accordion, { label: formatMessage({
                    id: "ui-br-legaldeposits.filter.publicationStatus",
                }), separator: false },
                React.createElement(Checkbox, { label: formatMessage({
                        id: "ui-br-legaldeposits.filter.showEnded",
                    }), checked: filters.showEnded, onChange: function (e) { return onShowEndedChange(e.target.checked); } }))),
            onLibraryStatusChange && (React.createElement(Accordion, { label: formatMessage({
                    id: "ui-br-legaldeposits.filter.libraryStatus",
                }), separator: false }, [
                PeriodicalLibraryStatusEnum.PENDING,
                PeriodicalLibraryStatusEnum.EXCLUDED,
            ].map(function (libraryStatus) { return (React.createElement(RadioButton, { key: libraryStatus, label: formatMessage({
                    id: "ui-br-legaldeposits.periodicals.status.".concat(libraryStatus),
                }), checked: filters.libraryStatus === libraryStatus, onChange: function () { return onLibraryStatusChange(libraryStatus); } })); })))),
        React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, type: "submit", disabled: !hasUnappliedChanges || lastReceivedYearInvalid },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.apply" }))));
};
